/**
 * Calls: every Support call, newest first, one row per call. Filters for missed calls (voicemail
 * included), today or this week, and a phone search. Refreshes every 30 s; Load more pages on.
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { type CallsListInput } from '@lsc/contract';
import { useState } from 'react';
import { openCapture } from '../components/capture/capture-store';
import { CallList } from '../components/calls/call-list';
import { useProvideAssistantContext } from '../lib/assistant-context';
import { describeRpcError, rpc } from '../lib/rpc';

export const Route = createFileRoute('/calls')({
  component: Calls,
});

type Outcome = NonNullable<CallsListInput['outcome']>;
type Since = NonNullable<CallsListInput['since']>;

const OUTCOMES: { value: Outcome; label: string }[] = [
  { value: 'all', label: 'All calls' },
  { value: 'missed', label: 'Missed' },
];
const SINCE: { value: Since; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'all', label: 'All time' },
];

function Calls() {
  const [outcome, setOutcome] = useState<Outcome>('all');
  const [since, setSince] = useState<Since>('week');
  const [phone, setPhone] = useState('');
  useProvideAssistantContext(null);
  const q = useInfiniteQuery({
    queryKey: ['calls.list', outcome, since, phone.replace(/\D/g, '')],
    queryFn: ({ pageParam }) => rpc.calls.list({ outcome, since, phone: phone.trim() || undefined, cursor: pageParam, limit: 50 }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 30_000,
  });
  const calls = q.data?.pages.flatMap((p) => p.calls) ?? [];
  const total = q.data?.pages[0]?.total ?? 0;

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="flex flex-wrap items-center gap-3 px-6 pt-4 pb-3">
        <h1 className="text-title font-medium">Calls</h1>
        <div role="tablist" aria-label="Which calls" className="flex items-center gap-1">
          {OUTCOMES.map((o) => (
            <button
              key={o.value}
              type="button"
              role="tab"
              aria-selected={outcome === o.value}
              onClick={() => {
                setOutcome(o.value);
              }}
              className={`h-8 rounded-control px-2.5 text-body ${outcome === o.value ? 'bg-surface-2 text-ink' : 'text-ink-2 hover:bg-surface-2 hover:text-ink'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <select
          aria-label="When"
          value={since}
          onChange={(e) => {
            setSince(e.target.value as Since);
          }}
          className="h-8 rounded-control border border-line bg-canvas px-2 text-body text-ink"
        >
          {SINCE.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          aria-label="Search by phone number"
          placeholder="Phone number"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
          }}
          className="h-8 w-48 rounded-control border border-line bg-canvas px-2.5 text-body text-ink placeholder:text-ink-3"
        />
      </div>
      {q.error ? (
        <div className="px-6 text-body text-alert">{describeRpcError(q.error)}</div>
      ) : !q.data ? (
        <div className="px-6 text-body text-ink-3">Loading calls</div>
      ) : (
        <CallList
          calls={calls}
          total={total}
          hasMore={q.hasNextPage}
          loadingMore={q.isFetchingNextPage}
          onMore={() => {
            void q.fetchNextPage();
          }}
          onLog={(c) => {
            openCapture({
              rootCallId: c.rootCallId,
              ...(c.externalNumber ? { phone: c.externalNumber } : {}),
              ...(c.account ? { accountId: c.account.id } : {}),
              ...(c.answeredByProfileId && c.answeredByName ? { answeredBy: { profileId: c.answeredByProfileId, name: c.answeredByName } } : {}),
            });
          }}
        />
      )}
    </div>
  );
}
